import { type ClassValue, clsx } from "clsx"
import { format, parse } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export function pluralize(
  word: string,
  count: number,
  {
    endsWithVowel,
    skipCount,
    plural,
  }: { endsWithVowel?: boolean; skipCount?: boolean; plural?: string } = {
    endsWithVowel: false,
    skipCount: false,
    plural: "",
  },
) {
  let str = `${count} `

  if (skipCount) {
    str = ""
  }

  if (plural) {
    return `${count !== 1 ? `${str}${plural}` : `${str}${word}`}`
  }

  return `${
    count !== 1 ? `${str}${word}${endsWithVowel ? "es" : "s"}` : `${str}${word}`
  }`
}

export const getPlatformURL = (
  platform: "suraasa" | "sso" | "learn",
  url: string,
) => {
  let domain

  if (platform === "suraasa") {
    domain = import.meta.env.VITE_SURAASA_PLATFORM_URL
  }
  if (platform === "sso") {
    domain = import.meta.env.VITE_SSO_URL
  }
  if (platform === "learn") {
    domain = import.meta.env.VITE_LEARN_PLATFORM_UR
  }

  return `${domain}${url}`
}

export const openFile = (href: string) => {
  const link = document.createElement("a")
  link.href = href
  link.target = "_blank"
  link.click()
  setTimeout(() => {
    window.URL.revokeObjectURL(href)
  }, 100)
}

export type ValueOf<T> = T[keyof T]

export const scrollTo = (id: string) => {
  const element = document.getElementById(id)
  if (!element) return

  element.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  })
}

export function formatTimeString(timeString: string, formatStr: string) {
  const parsedTime = parse(timeString, "HH:mm:ss", new Date())
  return format(parsedTime, formatStr)
}

export const getWorkDaysLabel = (days: string[]): string => {
  const dayMap: Record<string, string> = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  }

  const weekOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]

  const fullDays = days
    .map((day) => dayMap[day.toLowerCase()])
    .filter(Boolean)
    .sort((a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b))

  const isMonToFri =
    JSON.stringify(fullDays) === JSON.stringify(weekOrder.slice(0, 5))
  const isMonToSat =
    JSON.stringify(fullDays) === JSON.stringify(weekOrder.slice(0, 6))

  if (isMonToFri) return "Monday - Friday"
  if (isMonToSat) return "Monday - Saturday"

  return `${days.length} Working Days`
}

export const buildFullName = (user: {
  firstName: string
  lastName: string | null
}) => {
  return [user.firstName, user.lastName].filter(Boolean).join(" ")
}
export const formatDate = (
  date: string | undefined,
  formatStr = "MMMM d, yyyy",
) => (date ? format(new Date(date), formatStr) : "")

export const humaniseBytes = (bytes: number) => {
  const megabytes = bytes / (1024 * 1024)
  const formattedMB =
    megabytes % 1 === 0 ? megabytes.toFixed(0) : megabytes.toFixed(1)

  if (megabytes < 1) {
    return `${(megabytes * 1024).toFixed(0)}KB`
  }

  return `${formattedMB}MB`
}

export const MBToBytes = (mb: number) => mb * 1000000
/**
 * @param duration Format: "hh:mm:ss"
 */
export const humaniseDuration = (duration?: string) => {
  if (!duration) return undefined

  const roundToNearestFive = (num: number) => Math.round(num / 5) * 5

  const [hours, minutes, seconds] = duration.split(":").map(Number)

  const h = hours
  const m = roundToNearestFive(minutes)
  const s = roundToNearestFive(seconds)

  const parts: string[] = []
  if (h > 0) parts.push(pluralize("hour", h))
  if (m > 0) parts.push(pluralize("minute", m))
  if (s > 0 && hours === 0 && m === 0) parts.push(pluralize("second", s))

  return parts.join(" ")
}
